/**
 * Breadcrumb — fil d'Ariane partagé.
 *
 * @remarks
 * Navigation hiérarchique : chaque niveau cliquable (`to`) ramène en arrière,
 * le dernier item est l'élément courant (non cliquable, `aria-current="page"`).
 * Stylé via les tokens (pas de lib UI externe) — `Link` react-router +
 * `ChevronRight` comme séparateur.
 */

import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Icon } from '@/shared/ui';

export interface BreadcrumbItem {
  label: string;
  /** Cible de navigation. Absente → item courant (non cliquable). */
  to?: string | undefined;
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string | undefined;
}

export function Breadcrumb({ items, className = '' }: BreadcrumbProps) {
  return (
    <nav aria-label="Fil d'Ariane" className={`flex items-center gap-1 text-sm ${className}`}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isLink = item.to !== undefined && !isLast;

        return (
          <Fragment key={`${item.label}-${String(index)}`}>
            {index > 0 && (
              <Icon icon={ChevronRight} size="md" className="text-fg-subtle shrink-0" />
            )}
            {isLink ? (
              <Link
                to={item.to ?? ''}
                className="text-fg-muted hover:text-fg font-medium transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={isLast ? 'text-fg font-medium' : 'text-fg-muted font-medium'}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
